import {
  ChevronDownIcon,
  ChevronRightIcon,
  ClockIcon,
  CursorClickIcon,
  DuplicateIcon,
  InboxIcon,
  SearchIcon,
} from '@heroicons/react/outline';
import {useEffect, useMemo, useState} from 'react';
import {toast} from 'react-toastify';

import {Button, Input, SpinningLoader} from '@/components';
import {type TraceLogData, type TraceLogs} from '@/types/Observability';
import {getErrorMessage} from '@/utils/errorMessage';
import {copyToClipboard} from '@/utils/user';
import {
  getMethodColor,
  getStatusBadgeColor,
  getStatusTextColor,
} from '@/utils/userLogs';

import type {
  RecentTracesProps,
  SingleTraceLogProps,
  SingleTraceProps,
  TraceLogsProps,
  UserLogsDisplayProps,
} from './types';

const SingleTrace = ({trace, activeTraceId}: SingleTraceProps) => {
  return (
    <div
      className={`flex flex-col gap-2 hover:bg-blue-50 cursor-pointer border p-4 hover:shadow-sm rounded-md border-b border-gray-200 dark:border-gray-700 ${activeTraceId === trace.traceId ? 'bg-blue-50 dark:bg-blue-700' : 'bg-white dark:bg-gray-900'}`}
      title={trace.url}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 w-full">
          <span
            className={`px-2 py-0.5 text-xs font-medium rounded border ${getMethodColor(trace.method)}`}
          >
            {trace.method}
          </span>
          <span className="text-sm font-medium text-gray-900 dark:text-gray-200 truncate">
            {trace.traceId}
          </span>
          <div
            className={`w-2 h-2 rounded-full ${getStatusBadgeColor(trace.statusCode)}`}
          ></div>
        </div>
      </div>
      <div className="text-xs text-gray-600 dark:text-gray-400 mb-1 truncate w-full">
        {trace.url}
      </div>

      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <div className="flex items-center space-x-2">
          <ClockIcon className="w-3 h-3" />
          <span>{new Date(trace.timestamp).toLocaleString()}</span>
        </div>
        <div className="flex items-center space-x-2">
          <span
            className={`font-medium ${getStatusTextColor(trace.statusCode)}`}
          >
            {trace.statusCode}
          </span>
        </div>
      </div>
    </div>
  );
};

const RecentTraces = ({
  traces,
  setTraceId,
  activeTraceId,
  isLoading,
}: RecentTracesProps) => {
  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center h-screen">
        <SpinningLoader />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 space-y-2 px-4 pb-4 overflow-y-auto">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
        Recent Traces
      </h2>

      {/* Traces List */}
      {traces?.map((trace, index) => (
        <div key={index} onClick={() => setTraceId(trace.traceId)}>
          <SingleTrace trace={trace} activeTraceId={activeTraceId} />
        </div>
      ))}
    </div>
  );
};

const SingleTraceLog = ({data}: SingleTraceLogProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <div className="border-b dark:border-b-dark-borderMid">
      <div className="flex flex-row gap-3 hover:bg-gray-50 dark:hover:bg-dark-inputBg cursor-pointer rounded-md border-gray-100">
        <div className="p-2 flex flex-col gap-3 flex-1">
          <div
            className="px-4 py-3 cursor-pointer flex items-start gap-3"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <ChevronDownIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
            ) : (
              <ChevronRightIcon className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
            )}

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 text-sm mb-1">
                <span className="text-gray-500 dark:text-gray-300 font-mono text-xs">
                  {new Date(data.timestamp).toLocaleTimeString()}
                </span>
              </div>
            </div>
          </div>
          {isExpanded ? (
            <div className="pb-4">
              <div className="bg-gray-900 text-gray-100 p-3 rounded-md font-mono text-sm relative">
                <Button
                  variant="unStyled"
                  onClick={() => {
                    copyToClipboard(JSON.stringify(data));
                  }}
                  className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-200"
                  title="Copy to clipboard"
                >
                  <DuplicateIcon className="w-4 h-4" />
                </Button>
                <pre className="whitespace-pre-wrap pr-8">
                  {JSON.stringify(
                    {
                      timestamp: data.timestamp,
                      message: data.message,
                    },
                    null,
                    2,
                  )}
                </pre>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

const TraceLogs = ({traceId, fetchTraceLogs}: TraceLogsProps) => {
  const [traceLogs, setTraceLogs] = useState<TraceLogData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    setIsLoading(true);
    fetchTraceLogs(traceId)
      .then((logs) => setTraceLogs(logs.data))
      .catch((error: unknown) =>
        toast.error(getErrorMessage(error, 'Failed to load trace logs')),
      )
      .finally(() => setIsLoading(false));
  }, [traceId]);

  const filteredTraceLogs = useMemo(() => {
    if (!searchQuery.trim()) {
      return traceLogs; //all logs
    }
    const term = searchQuery.trim().toLowerCase();
    return traceLogs.filter((traceLog) =>
      traceLog.message.toLowerCase().includes(term),
    );
  }, [traceLogs, searchQuery]);

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center h-screen">
        <SpinningLoader />
      </div>
    );
  }

  if (traceLogs.length < 1) {
    return (
      <div className="flex flex-col flex-1 items-center justify-center py-10 text-center text-gray-500">
        <InboxIcon className="h-12 w-12 text-gray-400 mb-3" />
        <p className="text-md font-medium">
          No logs found for the Active Trace
        </p>
        <p className="text-sm text-gray-400 mt-1">Try running another trace.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 space-y-2 bg-white dark:bg-transparent justify-between">
      <div className="overflow-y-auto px-4 pb-4">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Trace {traceId}
        </h2>

        <div className="pb-5 border-b dark:border-b-dark-borderMid mb-3">
          <div className="w-2/4">
            <Input
              leftIcon={<SearchIcon className="w-4 h-4" />}
              type="text"
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {filteredTraceLogs.map((log, index) => (
          <SingleTraceLog key={index} data={log} />
        ))}
      </div>

      <div className="bg-white dark:bg-transparent border-t dark:border-t-dark-borderMid border-gray-200 p-6 flex items-center justify-between">
        <div className="text-sm text-gray-500">
          Showing {filteredTraceLogs.length} of {traceLogs.length} logs
        </div>
      </div>
    </div>
  );
};

const NoTraceSelected = () => {
  return (
    <div className="flex flex-col flex-1 items-center justify-center py-12 text-center text-gray-500">
      <CursorClickIcon className="h-12 w-12 text-gray-400 mb-3" />
      <p className="text-md font-medium">No trace selected</p>
      <p className="text-sm text-gray-400 mt-1">
        Please select a trace from the list to view its logs.
      </p>
    </div>
  );
};

const NoTracesAvailable = () => {
  return (
    <div className="flex flex-col flex-1 sm:border-b lg:border-r dark:border-dark-borderMid w-full lg:w-2/6 h-screen items-center justify-center py-12 text-center text-gray-500">
      <ClockIcon className="h-12 w-12 text-gray-400 mb-3" />
      <p className="text-md font-medium">No traces available</p>
      <p className="text-sm text-gray-400 mt-1">
        This customer has no recent traces to display.
      </p>
    </div>
  );
};

export default function UserLogsDisplay({
  traces,
  fetchTraceLogs,
  fetchTraces,
  isLoading,
  sliceTraces,
  cursor,
  canGoNext,
  canGoPrev,
}: UserLogsDisplayProps) {
  const [traceId, setTraceId] = useState<string>('');

  const isTracesEmpty = typeof traces === 'undefined' || traces.length < 1;

  // Reached once the locally sliced window runs out: there may still be a page
  // on the server behind `cursor`, so offer to fetch it.
  const fetchMoreButton =
    (!canGoNext || canGoPrev) && cursor ? (
      <Button
        variant="solid"
        disabled={isLoading}
        onClick={() => fetchTraces(cursor)}
      >
        Load more
      </Button>
    ) : null;

  return (
    <main className="flex flex-col lg:flex-row pt-5">
      {isTracesEmpty ? (
        <NoTracesAvailable />
      ) : (
        <div className="flex flex-col lg:w-2/6 border-r dark:border-dark-borderMid border-gray-200 lg:h-screen justify-between">
          <RecentTraces
            activeTraceId={traceId}
            traces={traces}
            setTraceId={setTraceId}
            isLoading={isLoading}
          />
          <div className="bg-white dark:bg-transparent border-t dark:border-t-dark-borderMid border-gray-200 p-4 flex items-center justify-between">
            <div>
              {canGoPrev ? (
                <Button
                  variant="solid"
                  disabled={isLoading}
                  onClick={() => sliceTraces('prev')}
                >
                  Load previous
                </Button>
              ) : null}
            </div>
            <div>
              {canGoNext ? (
                <Button
                  variant="solid"
                  disabled={isLoading}
                  onClick={() => sliceTraces('next')}
                >
                  Load more
                </Button>
              ) : (
                fetchMoreButton
              )}
            </div>
          </div>
        </div>
      )}
      {traceId?.trim() === '' ? (
        <NoTraceSelected />
      ) : (
        <TraceLogs traceId={traceId} fetchTraceLogs={fetchTraceLogs} />
      )}
    </main>
  );
}
